import Immutable from "immutable";
import { findIndex, isArray, isEqual, isNil, map } from "lodash";
import PropTypes from "prop-types";
import createCachedSelector from "re-reselect";
import React, { PureComponent } from "react";

import { immutableRecords, PropTypeShapes } from "../../types";
import { getScreenPointerPositionFromEvent } from "../../utils/mouseUtils";
import uniqueId from "../../utils/uniqueId";
import { ContainerContext, withContainerContext } from "../container/ContainerContext";
import calcDragPaneRects from "./calcDragPaneRects";
import calcPaneRects from "./calcPaneRects";
import SplitPane from "./SplitPane";
import { countChildren, getPaneSizes } from "./utils";

const SLOPPY_CLICK_DIST = 2;
const SLOPPY_CLICK_LAG = 350;

const testForClick = (downX, downY, downTime, upX, upY, upTime) =>
  upTime - downTime < SLOPPY_CLICK_LAG &&
  Math.abs(downX - upX) < SLOPPY_CLICK_DIST &&
  Math.abs(downY - upY) < SLOPPY_CLICK_DIST;

const toggleIfNeeded = (constraints, finalSizes, index, onExpand, onCollapse) => {
  if (constraints.get(index).canCollapse) {
    const cs = constraints.get(index).collapseSize;
    if (finalSizes[index] <= cs) {
      // Click was on a collapsed border, so let's open it
      if (onExpand) {
        onExpand(index, null);
      }
    } else {
      // Click was on an open border, so let's collapse it
      if (onCollapse) {
        onCollapse(index);
      }
    }
    return true;
  }
  return false;
};

const getContainerContextValue = createCachedSelector(
  (index, containerRects) => containerRects[index],
  (_index, _containerRects, paddingLeft) => paddingLeft,
  (_index, _containerRects, _paddingLeft, paddingRight) => paddingRight,
  (containerRect, paddingLeft, paddingRight) => {
    return {
      containerRect,
      paddingLeft,
      paddingRight
    };
  }
)(index => index);

class SplitView extends PureComponent {
  constructor(props, context) {
    super(props, context);
    this._debugId = uniqueId();
    this.state = { paneRects: [] };
    this.onResizerMouseMove = this.onResizerMouseMove.bind(this);
    this.onResizerMouseUp = this.onResizerMouseUp.bind(this);
    this.broadcastPaneSizes = this.broadcastPaneSizes.bind(this);
  }

  componentWillMount() {
    if (this.props.containerRect) {
      this.setPaneSizeState(this.props, calcPaneRects(this.props));
    }
  }

  componentWillReceiveProps(nextProps) {
    const origCount = countChildren(this.props.children);
    const nextCount = countChildren(nextProps.children);

    // Need to re-calculate layout
    if (
      nextProps.containerRect.width !== this.props.containerRect.width ||
      nextProps.containerRect.height !== this.props.containerRect.height ||
      nextProps.containerRect.top !== this.props.containerRect.top ||
      nextProps.vertical !== this.props.vertical ||
      nextCount !== origCount ||
      (nextProps.constraints !== this.props.constraints && !Immutable.is(nextProps.constraints, this.props.constraints))
    ) {
      this.refreshPaneSize(nextProps, nextCount !== origCount);
    }
  }

  componentWillUnmount() {
    document.removeEventListener("mousemove", this.onResizerMouseMove);
    document.removeEventListener("mouseup", this.onResizerMouseUp);
  }

  setPaneSizeState(nextProps, nextRects, isResizing = null) {
    // We need to calculate our containerRects now so that we don't reproduce them
    // every time in render() function
    const containerRects = map(nextRects, rect => {
      const { width, height, x, y } = rect;
      // Wrap the SplitPane in a container context so that everything knows its overall screen size.
      return immutableRecords.FlatRectRecord({
        left: nextProps.containerRect.left + x,
        width,
        right: nextProps.containerRect.left + x + width,
        top: nextProps.containerRect.top + y,
        height,
        bottom: nextProps.containerRect.top + y + height
      });
    });

    let obj = {
      paneRects: nextRects,
      containerRects
    };
    if (!isNil(isResizing)) {
      obj.isResizing = isResizing;
    }
    this.setState(obj);
  }

  refreshPaneSize(nextProps) {
    const nextRects = calcPaneRects(nextProps);
    this.setPaneSizeState(nextProps, nextRects);
    this.broadcastPaneSizes(nextRects);
  }

  broadcastPaneSizes(rects) {
    if (this.props.onResize) {
      const paneSizes = getPaneSizes(rects, this.props.vertical);
      this.props.onResize(paneSizes);
    }
  }

  onResizerMouseDown(splitIndex, e) {
    const constraintsArr = this.props.constraints.toArray();

    if (constraintsArr.length < splitIndex || !constraintsArr[splitIndex].canResize) {
      return;
    }
    const { x, y } = getScreenPointerPositionFromEvent(e);
    this._mouseDownIndex = splitIndex;
    this._mouseDownX = x;
    this._mouseDownY = y;
    this._mouseDownTime = performance.now();
    this._mouseDownRects = this.state.paneRects.map(s => {
      return { width: s.width, height: s.height, x: s.x, y: s.y };
    });
    this._mouseDownSizes = getPaneSizes(this.state.paneRects, this.props.vertical);

    document.addEventListener("mousemove", this.onResizerMouseMove);
    document.addEventListener("mouseup", this.onResizerMouseUp);
    document.addEventListener("touchmove", this.onResizerMouseMove);
    document.addEventListener("touchend", this.onResizerMouseUp);
  }

  onResizerMouseMove(e) {
    const { x, y } = getScreenPointerPositionFromEvent(e);
    const delta = this.props.vertical ? x - this._mouseDownX : y - this._mouseDownY;

    const beforeSizes = getPaneSizes(this.state.paneRects, this.props.vertical);
    const newRects = calcDragPaneRects(this.props, this._mouseDownIndex, this._mouseDownRects, delta);
    const afterSizes = getPaneSizes(newRects, this.props.vertical);

    this.setPaneSizeState(this.props, newRects, true);

    const collapsedIndex = findIndex(afterSizes, (s, i) => {
      const cs = this.props.constraints.get(i).collapseSize;
      return s <= cs && beforeSizes[i] > cs;
    });
    const expandedIndex = findIndex(afterSizes, (s, i) => {
      const cs = this.props.constraints.get(i).collapseSize;
      return s > cs && beforeSizes[i] <= cs;
    });

    if (!isNil(collapsedIndex) || !isNil(expandedIndex)) {
      if (this.props.onResize) {
        const paneSizes = getPaneSizes(this.state.paneRects, this.props.vertical);
        this.props.onResize(paneSizes);
      }
      if (this.props.onCollapse) {
        // Find a collapsed pane: a pane has collapsed if its size is now collapsedSize,
        // and its previous size was larger
        if (!isNil(collapsedIndex)) {
          this.props.onCollapse(collapsedIndex);
        }
      }
      if (this.props.onExpand) {
        // Find a collapsed pane: a pane has collapsed if its size is now collapsedSize,
        // and its previous size was larger
        if (!isNil(expandedIndex)) {
          this.props.onExpand(expandedIndex, afterSizes[expandedIndex]);
        }
      }
    }
  }

  onResizerMouseUp(e) {
    this.setState({
      isResizing: false
    });
    document.removeEventListener("mousemove", this.onResizerMouseMove);
    document.removeEventListener("mouseup", this.onResizerMouseUp);
    document.removeEventListener("touchmove", this.onResizerMouseMove);
    document.removeEventListener("touchend", this.onResizerMouseUp);

    if (this.props.onResize) {
      const paneSizes = getPaneSizes(this.state.paneRects, this.props.vertical);
      // Only send through the onResize if the sizes have changed
      if (!isEqual(paneSizes, this._mouseDownSizes)) {
        this.props.onResize(paneSizes);
      }
    }

    // Check to see if they clicked rather than dragged
    const { x, y } = getScreenPointerPositionFromEvent(e);
    const wasClick = testForClick(this._mouseDownX, this._mouseDownY, this._mouseDownTime, x, y, performance.now());
    if (wasClick) {
      const finalSizes = getPaneSizes(this.state.paneRects, this.props.vertical);
      const toggleLeft = toggleIfNeeded(
        this.props.constraints,
        finalSizes,
        this._mouseDownIndex,
        this.props.onExpand,
        this.props.onCollapse
      );
      if (!toggleLeft) {
        toggleIfNeeded(
          this.props.constraints,
          finalSizes,
          this._mouseDownIndex + 1,
          this.props.onExpand,
          this.props.onCollapse
        );
      }
    }
  }

  render() {
    const constraintsArr = this.props.constraints.toArray();

    const children = isArray(this.props.children) ? this.props.children : [this.props.children];
    const childCount = children.length;
    let classes = ["aeonSplitView", this.props.vertical ? "vertical" : "horizontal"];
    if (this.state.isResizing) {
      classes.push("isResizing");
    }
    let components = [];

    for (let i = 0; i < childCount; i++) {
      const paneKey = "pane-" + i;
      const borderKey = "border-" + i;
      const resizerKey = "resizer-" + i;

      const { width, height, x, y } = this.state.paneRects[i];
      const divX = x + (this.props.vertical ? width : 0);
      const divY = y + (this.props.vertical ? 0 : height);
      const divStyle = { transform: `translate(${divX}px, ${divY}px)` };

      // Wrap the SplitPane in a container context so that everything knows its overall screen size.
      const containerContextValue = getContainerContextValue(
        i,
        this.state.containerRects,
        this.props.paddingLeft,
        this.props.paddingRight
      );

      components.push(
        <ContainerContext.Provider key={paneKey} value={containerContextValue}>
          <SplitPane key={paneKey} position={{ x, y }} vertical={this.props.vertical} shadow={this.props.hasShadow}>
            {children[i]}
          </SplitPane>
        </ContainerContext.Provider>
      );

      if (constraintsArr.length > i && constraintsArr[i].hasDivider) {
        components.push(
          <div key={borderKey} className={`aeonSplitBorder ${constraintsArr[i].dividerVisible ? "visible" : ""}`} />
        );
        if (constraintsArr[i].canResize) {
          components.push(
            <div
              key={resizerKey}
              style={divStyle}
              className={`aeonSplitResizer ${this.state.isResizing && this._mouseDownIndex === i ? "dragging" : ""}`}
              onMouseDown={this.onResizerMouseDown.bind(this, i)}
              onTouchStart={this.onResizerMouseDown.bind(this, i)}
            />
          );
        }
      }
    }
    if (constraintsArr.length === childCount && constraintsArr[childCount - 1].hasDivider) {
      components.splice(-1, 1);
    }
    return <div className={classes.join(" ")}>{components}</div>;
  }
}

SplitView.propTypes = {
  children: PropTypeShapes.elementOrArray.isRequired,
  vertical: PropTypes.bool.isRequired,
  constraints: PropTypeShapes.splitConstraintsList.isRequired,
  hasShadow: PropTypes.bool,
  onResize: PropTypes.func,
  onCollapse: PropTypes.func,
  onExpand: PropTypes.func,

  // Comes from ContainerContext
  containerRect: PropTypeShapes.flatRect.isRequired,
  paddingLeft: PropTypes.number,
  paddingRight: PropTypes.number
};

export default withContainerContext(SplitView);
